import { useState, useEffect, useRef, useCallback } from "react";

// ===== TEMPORAL SMOOTHING PARAMETERS =====
const MIN_DURATION_FOR_FLAG = 1000; // 1 second continuous → flag
const VIOLATION_COOLDOWN = 10000; // 10 seconds between same-violation re-triggers
const GRACE_PERIOD = 2000; // 2 seconds before clearing state
const FRAME_TIME_MS = 33.33; // ~30 FPS

// =====AUDIO THRESHOLDS =====
const AUDIO_RMS_THRESHOLD = 0.018;
const MULTI_SPEAKER_STD_DEV_TH = 0.065;
const RMS_HISTORY_LENGTH = 15;

// ===== BLENDSHAPE THRESHOLDS (Google-trained ARKit scores, 0-1) =====
const GAZE_ON_THRESHOLD = 0.55; // score above this → looking away
const GAZE_OFF_THRESHOLD = 0.35; // score below this → looking back (hysteresis)

// ===== LIP-SYNC (jaw movement variance) =====
// We detect "lips not moving" by variance of jawOpen over a rolling window,
// NOT by absolute openness. A static parted mouth should still count as "not moving".
const JAW_HISTORY_LENGTH = 15; // ~0.5s at 30 FPS
const JAW_MOVEMENT_STDDEV_THRESHOLD = 0.015; // below = jaw is static (not moving)

// CDN URLs (no npm install needed — webpackIgnore keeps webpack from rewriting)
const TASKS_VISION_CDN =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/vision_bundle.mjs";
const WASM_ROOT =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
const FACE_LANDMARKER_MODEL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

const initialViolation = (category) => ({
  state: false,
  category,
  consecutiveFrames: 0,
  cooldownUntil: 0,
  graceUntil: 0,
  startTime: 0,
  duration: 0,
  confidence: 0,
  details: {},
});

export const useMediaPipeViolations = (videoRef, isRunning) => {
  const [violations, setViolations] = useState({
    speaking: false,
    violations: {
      NO_FACE: initialViolation("face"),
      MULTI_FACE: initialViolation("face"),
      LOOKING_AWAY: initialViolation("face"),
      LIPS_NOT_MOVING: initialViolation("voice"),
      MULTI_SPEAKER: initialViolation("voice"),
      TAB_SWITCH: initialViolation("browser"),
    },
  });

  const faceLandmarkerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const rmsHistoryRef = useRef([]);
  const jawHistoryRef = useRef([]); // rolling window of jawOpen values
  const rafRef = useRef(null);
  const lastFrameTimeRef = useRef(0);
  const gazeStateRef = useRef({ lookingAway: false }); // hysteresis

  // ===== Audio metrics =====
  const calculateAudioMetrics = useCallback(() => {
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    const rmsHistory = rmsHistoryRef.current;
    if (!analyser || !dataArray) return null;

    analyser.getFloatTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] ** 2;
    const rms = Math.sqrt(sum / dataArray.length);

    rmsHistory.push(rms);
    if (rmsHistory.length > RMS_HISTORY_LENGTH) rmsHistory.shift();

    if (rmsHistory.length < 5) {
      return {
        rms,
        averageRms: rms,
        standardDeviation: 0,
        isActive: rms > AUDIO_RMS_THRESHOLD,
      };
    }

    const avg = rmsHistory.reduce((a, b) => a + b, 0) / rmsHistory.length;
    const variance =
      rmsHistory.reduce((acc, v) => acc + (v - avg) ** 2, 0) /
      rmsHistory.length;
    const stdDev = Math.sqrt(variance);

    return {
      rms,
      averageRms: avg,
      standardDeviation: stdDev,
      isActive: avg > AUDIO_RMS_THRESHOLD,
    };
  }, []);

  // ===== Temporal smoothing state machine =====
  const updateViolationState = useCallback(
    (name, isTriggered, now, details = {}) => {
      setViolations((prev) => {
        const prevViol = prev.violations[name];
        const newViol = { ...prevViol };

        if (isTriggered) {
          newViol.consecutiveFrames = (prevViol.consecutiveFrames || 0) + 1;
          const durationMs = newViol.consecutiveFrames * FRAME_TIME_MS;

          if (
            durationMs >= MIN_DURATION_FOR_FLAG &&
            now > (prevViol.cooldownUntil || 0)
          ) {
            if (!prevViol.state) {
              newViol.state = true;
              newViol.startTime = now;
              newViol.cooldownUntil = now + VIOLATION_COOLDOWN;
              newViol.graceUntil = now + GRACE_PERIOD;
            }
            newViol.duration = durationMs;
            newViol.confidence = Math.min(
              durationMs / MIN_DURATION_FOR_FLAG,
              1.0
            );
            newViol.details = details;
          }
        } else {
          newViol.consecutiveFrames = 0;
          if (prevViol.state && now > (prevViol.graceUntil || 0)) {
            newViol.state = false;
          }
        }

        return { ...prev, violations: { ...prev.violations, [name]: newViol } };
      });
    },
    []
  );

  // ===== Blendshape helpers =====
  const getBlendshapeScore = (blendshapes, categoryName) => {
    const cat = blendshapes?.find((c) => c.categoryName === categoryName);
    return cat ? cat.score : 0;
  };

  // Gaze = max of the 8 eye-look blendshapes
  const detectLookingAway = useCallback((blendshapes) => {
    if (!blendshapes || blendshapes.length === 0)
      return { away: false, maxGaze: 0 };

    const gazeScores = [
      getBlendshapeScore(blendshapes, "eyeLookOutLeft"),
      getBlendshapeScore(blendshapes, "eyeLookOutRight"),
      getBlendshapeScore(blendshapes, "eyeLookInLeft"),
      getBlendshapeScore(blendshapes, "eyeLookInRight"),
      getBlendshapeScore(blendshapes, "eyeLookUpLeft"),
      getBlendshapeScore(blendshapes, "eyeLookUpRight"),
      getBlendshapeScore(blendshapes, "eyeLookDownLeft"),
      getBlendshapeScore(blendshapes, "eyeLookDownRight"),
    ];
    const maxGaze = Math.max(...gazeScores);

    const gs = gazeStateRef.current;
    if (maxGaze > GAZE_ON_THRESHOLD && !gs.lookingAway) gs.lookingAway = true;
    else if (maxGaze < GAZE_OFF_THRESHOLD && gs.lookingAway)
      gs.lookingAway = false;

    return { away: gs.lookingAway, maxGaze };
  }, []);

  // ===== Main effect =====
  useEffect(() => {
    if (!isRunning || !videoRef?.current) return;

    let cancelled = false;

    const init = async () => {
      try {
        // Dynamic ESM import from CDN — webpackIgnore keeps webpack from rewriting
        const vision = await import(/* webpackIgnore: true */ TASKS_VISION_CDN);
        const { FilesetResolver, FaceLandmarker } = vision;

        if (cancelled) return;

        const fileset = await FilesetResolver.forVisionTasks(WASM_ROOT);
        if (cancelled) return;

        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          fileset,
          {
            baseOptions: {
              modelAssetPath: FACE_LANDMARKER_MODEL,
              delegate: "GPU",
            },
            runningMode: "VIDEO",
            numFaces: 2,
            outputFaceBlendshapes: true,
            minFaceDetectionConfidence: 0.5,
            minFacePresenceConfidence: 0.5,
            minTrackingConfidence: 0.5,
          }
        );


        // Setup audio pipeline
        const video = videoRef.current;
        if (video?.srcObject && !audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();
          if (audioCtxRef.current.state === "suspended")
            await audioCtxRef.current.resume();
          analyserRef.current = audioCtxRef.current.createAnalyser();
          analyserRef.current.fftSize = 2048;
          analyserRef.current.smoothingTimeConstant = 0.8;
          const source = audioCtxRef.current.createMediaStreamSource(
            video.srcObject
          );
          source.connect(analyserRef.current);
          dataArrayRef.current = new Float32Array(analyserRef.current.fftSize);
        }

        // Detection loop
        const detectLoop = () => {
          if (cancelled) return;
          const video = videoRef.current;
          const fl = faceLandmarkerRef.current;

          if (!video || video.readyState < 2 || !fl) {
            rafRef.current = requestAnimationFrame(detectLoop);
            return;
          }

          const now = performance.now();
          if (now - lastFrameTimeRef.current < FRAME_TIME_MS) {
            rafRef.current = requestAnimationFrame(detectLoop);
            return;
          }
          lastFrameTimeRef.current = now;

          try {
            const result = fl.detectForVideo(video, now);
            const faceCount = result.faceLandmarks?.length || 0;
            const blendshapes = result.faceBlendshapes?.[0]?.categories || null;

            const audio = calculateAudioMetrics();
            const isAudioActive = audio?.isActive || false;
            const nowMs = Date.now();

            // Periodic status log
            if (Math.floor(nowMs / 3000) !== (window.__lastVioLog || 0)) {
              window.__lastVioLog = Math.floor(nowMs / 3000);
              const jaw = blendshapes
                ? getBlendshapeScore(blendshapes, "jawOpen")
                : 0;
            }

            // ===== FACE COUNT =====
            
            updateViolationState("NO_FACE", faceCount === 0, nowMs, {
              faceCount,
            });
            updateViolationState("MULTI_FACE", faceCount > 1, nowMs, {
              faceCount,
            });

            // ===== GAZE + LIPS (require blendshapes) =====
            if (blendshapes) {
              const { away, maxGaze } = detectLookingAway(blendshapes);
              updateViolationState("LOOKING_AWAY", away, nowMs, {
                maxGaze: maxGaze.toFixed(2),
              });

              // Track jawOpen movement (variance) over a rolling window.
              // Static jaw + active audio = someone else / recording is speaking.
              const jawOpen = getBlendshapeScore(blendshapes, "jawOpen");
              const jawHistory = jawHistoryRef.current;
              jawHistory.push(jawOpen);
              if (jawHistory.length > JAW_HISTORY_LENGTH) jawHistory.shift();

              let jawStdDev = 0;
              if (jawHistory.length >= 5) {
                const jawAvg =
                  jawHistory.reduce((a, b) => a + b, 0) / jawHistory.length;
                const jawVar =
                  jawHistory.reduce((acc, v) => acc + (v - jawAvg) ** 2, 0) /
                  jawHistory.length;
                jawStdDev = Math.sqrt(jawVar);
              }

              // Only flag when: audio is active AND jaw is essentially static.
              // Requires enough samples in the window to be reliable.
              const jawIsStatic =
                jawHistory.length >= JAW_HISTORY_LENGTH &&
                jawStdDev < JAW_MOVEMENT_STDDEV_THRESHOLD;
              const lipsNotMoving = isAudioActive && jawIsStatic;

              updateViolationState("LIPS_NOT_MOVING", lipsNotMoving, nowMs, {
                jawOpen: jawOpen.toFixed(3),
                jawStdDev: jawStdDev.toFixed(4),
                audioRms: audio?.averageRms?.toFixed(4),
              });
            }

            // ===== AUDIO =====
            if (audio) {
              const isMulti =
                audio.isActive &&
                audio.standardDeviation > MULTI_SPEAKER_STD_DEV_TH;
              setViolations((prev) =>
                prev.speaking === audio.isActive
                  ? prev
                  : { ...prev, speaking: audio.isActive }
              );
              updateViolationState("MULTI_SPEAKER", isMulti, nowMs, {
                stdDev: audio.standardDeviation.toFixed(4),
              });
            }
          } catch (err) {
            console.error("Detection error:", err);
          }

          rafRef.current = requestAnimationFrame(detectLoop);
        };

        detectLoop();
      } catch (err) {
        console.error("❌ Failed to init MediaPipe Tasks Vision:", err);
      }
    };

    // TAB_SWITCH - discrete event, bypass temporal smoothing
    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.hidden) {
        setViolations((prev) => {
          const prevViol = prev.violations.TAB_SWITCH;
          if (now < (prevViol.cooldownUntil || 0)) return prev;
          console.log("", { tabHidden: true });
          return {
            ...prev,
            violations: {
              ...prev.violations,
              TAB_SWITCH: {
                ...prevViol,
                state: true,
                startTime: now,
                cooldownUntil: now + VIOLATION_COOLDOWN,
                graceUntil: now + GRACE_PERIOD,
                duration: 0,
                confidence: 1,
                details: { tabHidden: true },
              },
            },
          };
        });
      } else {
        setViolations((prev) => {
          if (!prev.violations.TAB_SWITCH.state) return prev;
          return {
            ...prev,
            violations: {
              ...prev.violations,
              TAB_SWITCH: { ...prev.violations.TAB_SWITCH, state: false },
            },
          };
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    init();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (faceLandmarkerRef.current) {
        try {
          faceLandmarkerRef.current.close();
        } catch (e) {
          /* noop */
        }
        faceLandmarkerRef.current = null;
      }
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.close();
      }
      audioCtxRef.current = null;
      analyserRef.current = null;
      dataArrayRef.current = null;
      rmsHistoryRef.current = [];
      jawHistoryRef.current = [];
    };
  }, [
    videoRef,
    isRunning,
    calculateAudioMetrics,
    detectLookingAway,
    updateViolationState,
  ]);

  return violations;
};